import axios from 'axios';
import { Copy, CreditCard, Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../design-system/button/button';
import { Inline } from '../../design-system/inline/inline';
import { Stack } from '../../design-system/stack/stack';
import { UTCDate } from '../../utils/dates';

type SecureDetailsTokenResponse = {
  token: string;
  encryptedSymmetricKey: string;
  initialisationVector: string;
  secureDetailsUrl: string;
};

type SecureCardDetails = {
  pan: string;
  cvv2: string;
  pin?: string;
};

/**
 * Generates an RSA key pair suitable for the Modulr secure card details API.
 * Uses WebCrypto API to generate a 4096-bit RSA-OAEP key pair.
 */
const generateKeyPair = async () => {
  const keyPair = await crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: 'SHA-256',
    },
    true,
    ['encrypt', 'decrypt'],
  );
  return keyPair;
};

/**
 * Encodes the public key as a Base64 string for the Modulr API.
 */
const encodePublicKey = async (keyPair: CryptoKeyPair): Promise<string> => {
  const exported = await crypto.subtle.exportKey('spki', keyPair.publicKey);
  return btoa(String.fromCharCode(...new Uint8Array(exported)));
};

/**
 * Decrypts the access token returned by the Modulr API.
 * Uses the private key to decrypt the symmetric key, then uses the symmetric key
 * to decrypt the token using AES-GCM.
 */
const decryptAccessToken = async (
  encryptedToken: string,
  encryptedSymmetricKey: string,
  encodedIv: string,
  privateKey: CryptoKey,
): Promise<string> => {
  // Decode and decrypt the symmetric key using RSA-OAEP
  const decodedEncryptedSymmetricKey = Uint8Array.from(atob(encryptedSymmetricKey), (c) => c.charCodeAt(0));
  const decryptedSymmetricKey = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    decodedEncryptedSymmetricKey,
  );

  // Import the symmetric key as an AES-GCM key
  const aesKey = await crypto.subtle.importKey('raw', decryptedSymmetricKey, 'AES-GCM', true, ['encrypt', 'decrypt']);

  // Decode the IV and encrypted token
  const decodedIv = Uint8Array.from(atob(encodedIv), (c) => c.charCodeAt(0));
  const decodedEncryptedToken = Uint8Array.from(atob(encryptedToken), (c) => c.charCodeAt(0));

  // Decrypt the token using AES-GCM
  const rawDecryptedToken = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: decodedIv,
    },
    aesKey,
    decodedEncryptedToken,
  );

  return new TextDecoder('utf-8').decode(new DataView(rawDecryptedToken));
};

export const CardsTestPage = () => {
  const [cardId, setCardId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardDetails, setCardDetails] = useState<SecureCardDetails | null>(null);
  const [showPan, setShowPan] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs((prev) => [...prev, `${new UTCDate().toISOString().slice(11, 19)} - ${message}`]);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    addLog(`Copied ${label} to clipboard`);
  };

  const fetchSecureCardDetails = async () => {
    if (!cardId.trim()) {
      setError('Please enter a card ID');
      return;
    }

    setIsLoading(true);
    setError(null);
    setCardDetails(null);
    setLogs([]);

    try {
      // Step 1: Generate RSA key pair
      addLog('Generating RSA-4096 key pair...');
      const keyPair = await generateKeyPair();
      addLog('Key pair generated successfully');

      // Step 2: Encode the public key
      addLog('Encoding public key...');
      const publicKey = await encodePublicKey(keyPair);
      addLog(`Public key encoded (${publicKey.length} chars)`);

      // Step 3: Request secure details token from our server
      addLog('Requesting secure details token from server...');
      const tokenResponse = await axios.post<SecureDetailsTokenResponse>('/api/cards/secure-details-token', {
        cardId: cardId.trim(),
        publicKey,
      });
      addLog('Token received from server');

      const { token, encryptedSymmetricKey, initialisationVector, secureDetailsUrl } = tokenResponse.data;

      // Step 4: Decrypt the token
      addLog('Decrypting token...');
      const decryptedToken = await decryptAccessToken(
        token,
        encryptedSymmetricKey,
        initialisationVector,
        keyPair.privateKey,
      );
      addLog('Token decrypted successfully');

      // Step 5: Call Modulr's secure-detail endpoint directly
      addLog(`Fetching secure card details from ${secureDetailsUrl}...`);
      const detailsResponse = await fetch(secureDetailsUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          Accept: 'application/json',
        },
      });

      if (!detailsResponse.ok) {
        const errorText = await detailsResponse.text();
        throw new Error(`Modulr API error: ${detailsResponse.status} - ${errorText}`);
      }

      const details: SecureCardDetails = await detailsResponse.json();
      addLog('Secure card details retrieved successfully!');
      setCardDetails(details);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      addLog(`ERROR: ${message}`);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const maskValue = (value: string, showFull: boolean) => {
    if (showFull) return value;
    return value.replace(/./g, '•');
  };

  return (
    <Stack space={6} className="mx-auto max-w-2xl p-8">
      <Stack space={2}>
        <h1 className="text-2xl font-semibold">Secure Card Details Test</h1>
        <p className="text-muted-foreground">
          Test the Modulr secure card details API. Enter a card ID to retrieve the full PAN, CVV, and PIN.
        </p>
      </Stack>

      <Stack space={4}>
        <Stack space={2}>
          <label htmlFor="cardId" className="text-sm font-medium">
            Card ID
          </label>
          <input
            id="cardId"
            type="text"
            value={cardId}
            onChange={(e) => setCardId(e.target.value)}
            placeholder="card_xxxxxxxxxxxx"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          />
        </Stack>

        <Button onClick={fetchSecureCardDetails} isLoading={isLoading} iconLeft={<CreditCard className="h-4 w-4" />}>
          {isLoading ? 'Fetching...' : 'Fetch Secure Details'}
        </Button>
      </Stack>

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {cardDetails && (
        <Stack space={4} className="rounded-md border bg-card p-4">
          <h2 className="font-semibold">Card Details</h2>

          <Stack space={3}>
            <Inline space={2} align="center" justify="between">
              <Stack space={1}>
                <span className="text-xs text-muted-foreground">PAN (Card Number)</span>
                <code className="font-mono text-sm">{maskValue(cardDetails.pan, showPan)}</code>
              </Stack>
              <Inline space={1}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowPan(!showPan)}
                  aria-label={showPan ? 'Hide PAN' : 'Show PAN'}
                >
                  {showPan ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(cardDetails.pan, 'PAN')}
                  aria-label="Copy PAN"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </Inline>
            </Inline>

            <Inline space={2} align="center" justify="between">
              <Stack space={1}>
                <span className="text-xs text-muted-foreground">CVV</span>
                <code className="font-mono text-sm">{maskValue(cardDetails.cvv2, showCvv)}</code>
              </Stack>
              <Inline space={1}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowCvv(!showCvv)}
                  aria-label={showCvv ? 'Hide CVV' : 'Show CVV'}
                >
                  {showCvv ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(cardDetails.cvv2, 'CVV')}
                  aria-label="Copy CVV"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </Inline>
            </Inline>

            {cardDetails.pin && (
              <Inline space={2} align="center" justify="between">
                <Stack space={1}>
                  <span className="text-xs text-muted-foreground">PIN</span>
                  <code className="font-mono text-sm">{maskValue(cardDetails.pin, showPin)}</code>
                </Stack>
                <Inline space={1}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPin(!showPin)}
                    aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                  >
                    {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyToClipboard(cardDetails.pin!, 'PIN')}
                    aria-label="Copy PIN"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </Inline>
              </Inline>
            )}
          </Stack>
        </Stack>
      )}

      {logs.length > 0 && (
        <Stack space={2} className="rounded-md border bg-muted/50 p-4">
          <h3 className="text-sm font-medium">Logs</h3>
          <div className="max-h-48 overflow-y-auto">
            <Stack space={1}>
              {logs.map((log, index) => (
                <code key={index} className="block text-xs text-muted-foreground">
                  {log}
                </code>
              ))}
            </Stack>
          </div>
        </Stack>
      )}
    </Stack>
  );
};
